// services/productService.js
import api from './api';

/**
 * Service d'accès à l'API Produits marketplace.
 * Base URL: /products (prefix /api/v1 géré par le wrapper api)
 */

const productService = {
  /**
   * Recherche publique de produits.
   * @param {Object} params - { search, category_id, lat, lng, radius_km, status, page, limit }
   */
  search: async (params = {}) => {
    const response = await api.get('/products', { params });
    return response.data;
  },

  /**
   * Récupérer un produit par ID.
   */
  getById: async (productId) => {
    const response = await api.get(`/products/${productId}`);
    return response.data;
  },

  /**
   * Récupérer les produits du vendeur connecté.
   * @param {Object} params - { page, limit, status }
   */
  getMyProducts: async (params = {}) => {
    const response = await api.get('/products/my-products', { params });
    return response.data;
  },

  /**
   * Créer un nouveau produit (FormData pour gérer les images).
   * @param {FormData} formData
   */
  create: async (formData) => {
    const response = await api.post('/products', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  /**
   * Mettre à jour un produit.
   * @param {string} productId
   * @param {FormData} formData
   */
  update: async (productId, formData) => {
    const response = await api.put(`/products/${productId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  /**
   * Supprimer un produit.
   */
  delete: async (productId) => {
    const response = await api.delete(`/products/${productId}`);
    return response.data;
  },

  /**
   * Mettre en pause plusieurs produits (bulk).
   * @param {string[]} ids
   */
  bulkPause: async (ids) => {
    const response = await api.put('/products/bulk-pause', { ids });
    return response.data;
  },

  /**
   * Supprimer plusieurs produits (bulk).
   * @param {string[]} ids
   */
  bulkDelete: async (ids) => {
    const response = await api.delete('/products/bulk-delete', { data: { ids } });
    return response.data;
  },

  // ─── Créneaux de disponibilité ───────────────────────────────────────────

  /**
   * Récupérer les créneaux d'un produit.
   * @param {string} productId
   * @param {Object} params - { from, to, mode } (mode='subslots' pour les sous-créneaux détaillés)
   */
  getSlots: async (productId, params = {}) => {
    const response = await api.get(`/products/${productId}/availability`, { params });
    return response.data;
  },

  /**
   * Créer un créneau pour un produit.
   * @param {string} productId
   * @param {Object} data - { date, start_time, end_time, slot_duration_minutes, capacity }
   */
  createSlot: async (productId, data) => {
    const response = await api.post(`/products/${productId}/availability`, data);
    return response.data;
  },

  /**
   * Mettre à jour un créneau.
   */
  updateSlot: async (slotId, data) => {
    const response = await api.put(`/products/slots/${slotId}`, data);
    return response.data;
  },

  /**
   * Supprimer un créneau.
   */
  deleteSlot: async (slotId) => {
    const response = await api.delete(`/products/slots/${slotId}`);
    return response.data;
  },

  // ─── Périodes d'indisponibilité ──────────────────────────────────────────

  listUnavailabilityPeriods: async (productId) => {
    const response = await api.get(`/products/${productId}/unavailability`);
    return response.data;
  },

  createUnavailabilityPeriod: async (productId, data) => {
    const response = await api.post(`/products/${productId}/unavailability`, data);
    return response.data;
  },

  updateUnavailabilityPeriod: async (periodId, data) => {
    const response = await api.put(`/products/unavailability/${periodId}`, data);
    return response.data;
  },

  deleteUnavailabilityPeriod: async (periodId) => {
    const response = await api.delete(`/products/unavailability/${periodId}`);
    return response.data;
  }
};

export default productService;
